
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  logError?: boolean;
  component?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Toast wrapper for error reporting
const ErrorToaster = ({ error }: { error: Error }) => {
  const { toast } = useToast();
  
  React.useEffect(() => {
    toast({
      title: "An error occurred",
      description: error.message || "Something went wrong",
      variant: "destructive"
    });
  }, [error, toast]);
  
  return null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true, 
      error: error, 
      errorInfo: null 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Set errorInfo in state for reporting
    this.setState({ errorInfo });
    
    // Send error to monitoring service if logError is enabled
    if (this.props.logError) {
      // In production, this would connect to an error monitoring service
      // For now, this is handled silently to avoid console pollution
    }
  }

  handleReset = (): void => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    
    if (hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return (
          <>
            {error && this.props.logError && <ErrorToaster error={error} />}
            {this.props.fallback}
          </>
        );
      }
      
      // Default error UI with more detailed information
      return (
        <div className="p-4">
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription className="mt-2">
              <div className="space-y-2">
                <p>{error?.message || 'An unexpected error occurred'}</p>
                
                {process.env.NODE_ENV === 'development' && errorInfo && (
                  <div className="mt-4 rounded bg-muted/30 p-2">
                    <p className="text-xs font-mono overflow-auto max-h-32">
                      {errorInfo.componentStack}
                    </p>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
          
          <div className="flex justify-end">
            <Button 
              onClick={this.handleReset}
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
          </div>
          
          {/* Show error toaster if logError is enabled */}
          {error && this.props.logError && <ErrorToaster error={error} />}
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for functional components
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) => {
  const displayName = Component.displayName || Component.name || 'Component';
  
  const ComponentWithErrorBoundary = (props: P) => {
    return (
      <ErrorBoundary {...errorBoundaryProps} component={displayName}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
  
  ComponentWithErrorBoundary.displayName = `WithErrorBoundary(${displayName})`;
  
  return ComponentWithErrorBoundary;
};

export default ErrorBoundary;
